import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Mail, Phone, MapPin, ArrowRight, CheckCircle2 } from 'lucide-react';


import FAQSection from "../components/FAQSection";
import { contactFaqs } from "../data/faqs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
    }
    setLoading(false);
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Contact Us | Novaseeds Technologies</title>
        <meta name="description" content="Get in touch with Novaseeds Technologies. Share your project ideas or inquiries, and our team will connect with you promptly." />
      </Helmet>

      <div className="min-h-screen pt-32 pb-20 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          
          {/* Hero */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-muted-foreground font-bold">CONTACT US</span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mt-4 mb-6">
              <span className="text-foreground">Let's Start a</span><br />
              <span className="text-gradient">Conversation</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              Have a project in mind? We'd love to hear from you. Get in touch and let's build something amazing together.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <div className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-8 rounded-3xl">
                <h2 className="text-2xl font-semibold text-foreground mb-6">Send Us a Message</h2>

                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 size={40} className="text-green-600" />
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Thank You!</h3>
                    <p className="text-muted-foreground">We've received your message and will get back to you within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                      <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">Your Name *</label>
                      <input
                        type="text"
                        id="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full"
                        placeholder="John Doe"
                      />
                    </div>

                    <div className="mb-6">
                      <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full"
                        placeholder="john@company.com"
                      />
                    </div>

                    <div className="mb-6">
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">Subject *</label>
                      <input
                        type="text"
                        id="subject"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full"
                        placeholder="Project Inquiry"
                      />
                    </div>

                    <div className="mb-6">
                      <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">Message *</label>
                      <textarea
                        id="message"
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={5}
                        className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full resize-none"
                        placeholder="Tell us about your project..."
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-4 font-semibold shadow-[0_2px_8px_rgba(37,99,235,0.25)] transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      <span>{loading ? 'Sending...' : 'Send Message'}</span>
                      {!loading && <ArrowRight size={18} />}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="space-y-8">
              
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-6">Get In Touch</h2>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  Whether you have a question about our services, pricing, or anything else, our team is ready to answer all your questions.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
                      <Mail size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-foreground font-semibold mb-1">Email</h3>
                      <a href="mailto:sales@nvseeds.com" className="text-muted-foreground hover:text-blue-600 transition-colors">sales@nvseeds.com</a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
                      <Phone size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-foreground font-semibold mb-1">Phone</h3>
                      <a href="tel:+919203190441" className="text-muted-foreground hover:text-blue-600 transition-colors">+91-9203190441</a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-foreground font-semibold mb-1">Head Office</h3>
                      <p className="text-muted-foreground">
                        2nd floor, WeWork Eleven West,<br />
                        Pan Card Club Rd, Baner Gaon,<br />
                        Baner, Pune, Maharashtra 411069<br />
                        India
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-8 rounded-3xl">
                <h3 className="text-xl font-semibold text-foreground mb-4">Office Hours</h3>
                <div className="space-y-3 text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span className="font-medium text-foreground">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span className="font-medium text-foreground">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span className="font-medium text-foreground">Closed</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-3xl p-8">
                <h3 className="text-xl font-semibold text-foreground mb-3">Looking for Career Opportunities?</h3>
                <p className="text-muted-foreground mb-4">We're always looking for talented individuals to join our team.</p>
                <a href="mailto:careers@nvseeds.com" className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center space-x-1 hover:underline">
                  <span>View Open Positions</span>
                  <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>
          </div>
 {/* FAQ Section */}
<FAQSection faqs={contactFaqs} title="Frequently Asked Questions" />
        </div>
      </div>
    </>
  );
};
